/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createSelector } from 'reselect';

import { ApiStatus, CustomRole } from '../../../../ts';
import {
  Instance,
  InstanceInfo,
  InstanceLimits,
  ToolingData
} from '../../../../ts/instance';
import { PlanMetering, PlanName, TrialData } from '../../../../ts/plan';
import { TaskListKey } from '../../../../ts/preferences';
import { RootState } from '../../../../ts/state';
import { isEnterprisePlan } from '../../util/plan';
import { selectInstanceById } from '../instances/selectors';

/**
 * Selector to test whether instance is being loaded
 */
export const isLoadingInstance = (state: RootState): boolean => {
  return state.instance?.getStatus === ApiStatus.LOADING;
};

/**
 * Selector to test whether instance is being updated
 */
export const isUpdatingInstance = (state: RootState): boolean => {
  return state.instance?.updateStatus === ApiStatus.LOADING;
};

/**
 * Selector to get the current instance
 */
export const selectInstance = (state: RootState): InstanceInfo =>
  state.instance?.instance;

export const selectInstanceCustomRoles = (state: RootState): CustomRole[] =>
  state.instance?.instance_custom_roles || [];

/**
 * Retrieves the complete instance by checking the session for the current instance's region and instance ID
 * and returns the corresponding entry from the entries property.
 *
 */
export const selectCompleteInstance = (state: RootState): Instance => {
  const instance = selectInstance(state);
  // Need to format the instanceID correctly.
  return selectInstanceById(state, `${instance?.region}:${instance?.crn_id}`);
};

/**
 * Selector to see if the current instance should be rendered as New IA
 * or Classic IA.
 */
export const selectIsNewIa = (state: RootState): boolean =>
  Boolean(state.instance?.new_ia);

export const selectToolingData = (state: RootState): ToolingData =>
  state.instance?.tooling_data || {};

/**
 * If Store supports the "tooling_data" property (which is an empty object before
 * a trial is started), then return the plus trial information or an empty
 * object (which indicates to the caller that Store supports tooling data, but
 * there is no plus trial information).
 * If Store does NOT support the "tooling_data" property (i.e. if it is
 * undefined), then we will return null to let the caller no there is no
 * tooling data support.
 *
 * @return {Object|{}|null} An object representing the plus trial information,
 * an empty object, or null.
 */
export const selectPlusTrialData = (state: RootState): TrialData | null =>
  Boolean(state.instance?.tooling_data)
    ? selectToolingData(state).plus_trial || {}
    : null;

export const selectLimits = (state: RootState): InstanceLimits | undefined =>
  state.instance?.limits;

export const selectPlanId = (state: RootState): string | undefined =>
  state.instance?.plan.plan_id;

export const selectPlan = (
  state: RootState,
  def = PlanName.STANDARD
): PlanName => state.instance?.plan?.name ?? def;

export const selectPlanMetering = (
  state: RootState
): PlanMetering | undefined => state.instance?.plan?.metering;

export const selectIsEnterprisePlan = createSelector(
  [selectPlan],
  (plan: PlanName): boolean => isEnterprisePlan(plan)
);

export const selectIsPremiumPlan = createSelector(
  [selectPlan],
  (plan: PlanName): boolean =>
    plan === PlanName.PREMIUM ||
    plan === PlanName.PREMIUM_USERBASED ||
    plan === PlanName.DEV_ONLY_PREMIUM
);

/**
 * Returns true if the number of elements in `items` is greater
 * than the allowed by the plan
 *
 * @returns True if items.length is greater than the allowed in plan
 */
export const checkPlanLimit = (
  state: RootState,
  type: keyof InstanceLimits,
  items: never[],
  plans: PlanName[]
): boolean => {
  let result = false;
  const planName = selectPlan(state);
  const planLimits = selectLimits(state);
  if (!plans || plans.includes(planName as PlanName)) {
    const limit = planLimits[type] || 0;
    result = items?.length && items.length >= limit;
  }
  return !!result;
};

export const selectResourceGroupId = (state: RootState): string | undefined => {
  return state?.instance?.instance?.resource_group_id;
};

export const selectResourceGroupIdForPremiumPlan = (
  state: RootState
): string | undefined => {
  const plan = selectPlan(state);
  if (
    plan === PlanName.PREMIUM ||
    plan === PlanName.ENTERPRISE_DATA_ISOLATION
  ) {
    return selectResourceGroupId(state);
  }
};

export const selectOnboardingTaskListStatus = (state: RootState): boolean => {
  return Boolean(
    state?.instance?.instance_preferences?.taskListStatus?.[
      TaskListKey.HAS_FINISHED_ONBOARDING_TASK_LIST
    ]
  );
};

export const selectHasConvoSkillProvider = (state: RootState): boolean =>
  Boolean(
    state?.instance?.conversational_skill_provider?.provider_id ||
      state?.instance?.conversational_skill_providers?.length
  );
